import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot as BotIcon, Play, Square, Edit, Trash2, Eye,
  TrendingUp, TrendingDown, Clock, AlertCircle,
  Activity, Coins, TestTube, MoreVertical, ArrowUpRight,
  ArrowDownRight, Minus, Wallet, BarChart3
} from 'lucide-react';
import { Bot, BotStatus, Position } from '../types/api';
import { botAPI } from '../services/api';
import BotDetailModal from './BotDetailModal';
import EditBotModal from './EditBotModal';
import { Button } from './ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn, formatCurrency, formatPercentage } from '../lib/utils';
import toast from 'react-hot-toast';

interface BotCardProps {
  bot: Bot;
  status?: BotStatus;
  onStart: () => void;
  onStop: () => void;
  onDelete: () => void;
  onUpdate: (bot: Bot) => void;
  isStarting?: boolean;
  isStopping?: boolean;
}

const BotCard: React.FC<BotCardProps> = ({ bot, status, onStart, onStop, onDelete, onUpdate, isStarting = false, isStopping = false }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await botAPI.deleteBot(bot.id);
      onDelete();
    } catch (error) {
      console.error('Failed to delete bot:', error);
      toast.error('Failed to delete bot');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getStatusBadge = () => {
    if (!status) return { text: 'Unknown', variant: 'outline' as const };
    if (status.is_running) return { text: 'Running', variant: 'success' as const };
    if (status.startup_phase === 'starting') return { text: 'Starting', variant: 'warning' as const };
    if (status.startup_phase === 'failed' || status.error_message) return { text: 'Error', variant: 'destructive' as const };
    return { text: 'Stopped', variant: 'secondary' as const };
  };

  const statusBadge = getStatusBadge();
  
  // Calculate portfolio metrics
  const portfolioValue = status?.portfolio_state?.total_value || bot.config.trading_capital;
  const pnl = status?.portfolio_state?.total_pnl || 0;
  const pnlPercentage = status?.portfolio_state?.return_percentage || 0;

  return (
    <>
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-border/50 bg-card/50 backdrop-blur">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "p-2 rounded-lg",
                  status?.is_running ? "bg-green-500/10" : "bg-gray-500/10"
                )}>
                  <BotIcon className={cn(
                    "h-5 w-5",
                    status?.is_running ? "text-green-500" : "text-gray-500"
                  )} />
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold">{bot.name}</CardTitle>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant={statusBadge.variant} className="text-xs">
                      {status?.is_running && (
                        <motion.div
                          className="w-2 h-2 bg-green-500 rounded-full mr-1"
                          animate={{ opacity: [1, 0.5, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                      )}
                      {statusBadge.text}
                    </Badge>
                    {bot.config.testnet && (
                      <Badge variant="warning" className="text-xs">
                        <TestTube className="w-3 h-3 mr-1" />
                        Testnet
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowDetailModal(true)}>
                    <Eye className="mr-2 h-4 w-4" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowEditModal(true)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit Bot
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Bot
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Main Stats Row - Portfolio Value & Total P&L */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Portfolio</span>
                  <Wallet className="h-3 w-3 text-muted-foreground" />
                </div>
                <p className="text-lg font-bold">{formatCurrency(portfolioValue)}</p>
              </div>

              <div className={cn(
                "rounded-lg p-3 relative overflow-hidden",
                pnl >= 0 ? "bg-green-500/10" : "bg-red-500/10"
              )}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Total P&L</span>
                  {pnl >= 0 ? (
                    <TrendingUp className="h-3 w-3 text-green-500" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-red-500" />
                  )}
                </div>
                <p className={cn(
                  "text-lg font-bold",
                  pnl >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                )}>
                  {pnl >= 0 ? '+' : ''}{formatPercentage(pnlPercentage)}
                </p>
                <p className={cn(
                  "text-xs",
                  pnl >= 0 ? "text-green-600/70 dark:text-green-400/70" : "text-red-600/70 dark:text-red-400/70"
                )}>
                  {pnl >= 0 ? '+' : ''}{formatCurrency(pnl)}
                </p>
              </div>
            </div>

            {/* Active Positions Section */}
            {status?.is_running && status.current_positions && status.current_positions.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <BarChart3 className="h-3 w-3" />
                    Active Positions ({status.current_positions.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {status.current_positions.slice(0, 3).map((position: Position) => (
                    <div
                      key={position.symbol}
                      className={cn(
                        "rounded-lg p-2.5 border transition-colors",
                        position.profit_loss_pct >= 0
                          ? "bg-green-500/5 border-green-500/20 hover:border-green-500/40"
                          : "bg-red-500/5 border-red-500/20 hover:border-red-500/40"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "w-1.5 h-8 rounded-full",
                            position.profit_loss_pct >= 0 ? "bg-green-500" : "bg-red-500"
                          )} />
                          <div>
                            <p className="text-sm font-semibold">{position.symbol}</p>
                            <p className="text-xs text-muted-foreground">
                              Entry: {formatCurrency(position.entry_price)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={cn(
                            "flex items-center gap-1 text-sm font-bold",
                            position.profit_loss_pct >= 0
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          )}>
                            {position.profit_loss_pct >= 0 ? (
                              <ArrowUpRight className="h-3 w-3" />
                            ) : (
                              <ArrowDownRight className="h-3 w-3" />
                            )}
                            {position.profit_loss_pct >= 0 ? '+' : ''}{position.profit_loss_pct.toFixed(2)}%
                          </div>
                          <p className={cn(
                            "text-xs",
                            position.profit_loss_pct >= 0
                              ? "text-green-600/70 dark:text-green-400/70"
                              : "text-red-600/70 dark:text-red-400/70"
                          )}>
                            {position.profit_loss >= 0 ? '+' : ''}{formatCurrency(position.profit_loss)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {status.current_positions.length > 3 && (
                    <p className="text-xs text-muted-foreground text-center">
                      +{status.current_positions.length - 3} more position{status.current_positions.length - 3 > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
            ) : status?.is_running ? (
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <Minus className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">No active positions</p>
                <p className="text-xs text-muted-foreground/70">Bot is scanning for opportunities...</p>
              </div>
            ) : null}

            {/* Bot Info - Compact when running */}
            <div className="space-y-2 pt-2 border-t border-border/50">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Coins className="h-3 w-3" />
                  Pairs
                </span>
                <span className="font-medium text-muted-foreground">
                  {bot.config.coins.length <= 3
                    ? bot.config.coins.join(', ')
                    : `${bot.config.coins.slice(0, 2).join(', ')} +${bot.config.coins.length - 2}`
                  }
                </span>
              </div>

              {status?.is_running && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Uptime
                  </span>
                  <span className="font-medium text-muted-foreground">{formatUptime(status.uptime)}</span>
                </div>
              )}

              {/* Last Trade - Show if available */}
              {status?.recent_trades && status.recent_trades.length > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Activity className="h-3 w-3" />
                    Last Trade
                  </span>
                  <span className={cn(
                    "font-medium",
                    status.recent_trades[0].action === 'buy' ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                  )}>
                    {status.recent_trades[0].action.toUpperCase()} {status.recent_trades[0].symbol}
                  </span>
                </div>
              )}
            </div>

            {/* Startup Phase Indicator */}
            <AnimatePresence>
              {status?.startup_phase === 'starting' && !status.is_running && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3"
                >
                  <div className="flex items-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <Activity className="h-4 w-4 text-amber-500" />
                    </motion.div>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Bot is starting up... This may take a few seconds.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error Message */}
            <AnimatePresence>
              {(status?.error_message || status?.startup_phase === 'failed') && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-destructive/10 border border-destructive/20 rounded-lg p-3"
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-destructive">
                        {status?.startup_phase === 'failed' ? 'Failed to start' : 'Error'}
                      </p>
                      <p className="text-xs text-destructive/80 mt-0.5 break-words">
                        {status?.error_message || 'An unexpected error occurred'}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={onStart}
                    variant="outline"
                    size="sm"
                    className="mt-2 w-full text-xs h-7 border-destructive/30 hover:bg-destructive/10"
                    disabled={isStarting}
                  >
                    <Play className="h-3 w-3 mr-1" />
                    Retry
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>

          <CardFooter className="pt-4">
            <div className="flex gap-2 w-full">
              {status?.is_running ? (
                <Button
                  onClick={onStop}
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                  loading={isStopping}
                  disabled={isStopping}
                >
                  {!isStopping && <Square className="mr-2 h-4 w-4" />}
                  {isStopping ? 'Stopping...' : 'Stop Bot'}
                </Button>
              ) : (
                <Button
                  onClick={onStart}
                  variant="default"
                  size="sm"
                  className="flex-1"
                  loading={isStarting}
                  disabled={isStarting}
                >
                  {!isStarting && <Play className="mr-2 h-4 w-4" />}
                  {isStarting ? 'Starting...' : 'Start Bot'}
                </Button>
              )}
              
              <Button
                onClick={() => setShowDetailModal(true)}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                <Eye className="mr-2 h-4 w-4" />
                Details
              </Button>
            </div>
          </CardFooter>
        </Card>
      </motion.div>

      {/* Delete Confirmation Dialog */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card rounded-lg p-6 max-w-sm w-full shadow-xl border"
            >
              <h3 className="text-lg font-semibold mb-2">Delete Bot</h3>
              <p className="text-muted-foreground mb-4">
                Are you sure you want to delete "{bot.name}"? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowDeleteConfirm(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  variant="destructive"
                  className="flex-1"
                  loading={deleting}
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && (
          <BotDetailModal
            bot={bot}
            onClose={() => setShowDetailModal(false)}
          />
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && (
          <EditBotModal
            bot={bot}
            onClose={() => setShowEditModal(false)}
            onBotUpdated={(updatedBot) => {
              onUpdate(updatedBot);
              setShowEditModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default BotCard;